import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarDays, Images, Receipt, Wallet } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { EventForm } from "@/components/EventForm";
import { AdminCoupons } from "@/components/AdminCoupons";
import { AdminWatermark } from "@/components/AdminWatermark";
import { AdminNotify } from "@/components/AdminNotify";
import { toast } from "sonner";
import { api, brl, fmtDate, thumbUrl, errMsg, CATEGORIES } from "@/lib/api";

function Stat({ icon: Icon, label, value, id }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5">
      <Icon className="w-5 h-5 text-[#0055FF]" />
      <div className="mt-3 label-caps text-zinc-500">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold" data-testid={`stat-${id}`}>{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(false);
  const [tab, setTab] = useState("eventos");

  const load = () => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  };
  useEffect(load, []);

  const refund = async (o) => {
    if (!window.confirm(`Reembolsar ${brl(o.amount)} para ${o.email}? O cliente perde o acesso aos downloads.`)) return;
    try {
      await api.post(`/admin/orders/${o.session_id}/refund`);
      toast.success("Reembolso feito. O valor volta ao cliente em alguns dias.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <AdminShell>
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="label-caps text-zinc-500">Painel</span>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Olá, fotógrafo</h1>
        </div>
        <button onClick={() => setForm(true)} data-testid="new-event-btn" className="h-11 px-5 rounded-md bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Novo evento
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={CalendarDays} label="Eventos" value={stats.events} id="events" />
          <Stat icon={Images} label="Fotos" value={stats.photos} id="photos" />
          <Stat icon={Receipt} label="Fotos vendidas" value={stats.photos_sold} id="sold" />
          <Stat icon={Wallet} label="Faturamento" value={brl(stats.revenue)} id="revenue" />
        </div>
      )}

      <div className="mt-10 flex overflow-x-auto border-b border-zinc-200">
        {[["eventos", "Eventos"], ["pedidos", "Pedidos"], ["cupons", "Cupons"], ["marca", "Configurações"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`shrink-0 h-11 px-4 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-[#0055FF]" : "border-transparent text-zinc-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "eventos" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-events-list">
          {events.length === 0 && <p className="text-zinc-500">Nenhum evento ainda. Crie o primeiro!</p>}
          {events.map((e) => (
            <Link key={e.id} to={`/admin/eventos/${e.id}`} data-testid={`admin-event-${e.id}`} className="bg-white border border-zinc-200 rounded-lg overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow]">
              <div className="h-36 bg-zinc-100">{e.cover_photo_id && <img src={thumbUrl(e.cover_photo_id)} alt="" className="w-full h-full object-cover" />}</div>
              <div className="p-4">
                <div className="label-caps text-zinc-500">{CATEGORIES[e.category]}</div>
                <div className="mt-1 font-semibold">{e.name}</div>
                <div className="mt-1 text-sm text-zinc-500">{fmtDate(e.date)} · {e.photo_count} fotos · {brl(e.price)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "cupons" && <AdminCoupons />}
      {tab === "marca" && (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <AdminWatermark />
          <AdminNotify />
        </div>
      )}

      {tab === "pedidos" && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="admin-orders-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr>{["Data", "Cliente", "Fotos", "Valor", "Cupom", "Status", ""].map((h) => <th key={h} className="px-4 py-3 label-caps text-zinc-500">{h}</th>)}</tr>
            </thead>
            <tbody>
              {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-zinc-500">Nenhum pedido ainda.</td></tr>}
              {orders.map((o) => (
                <tr key={o.session_id} className="border-t border-zinc-200">
                  <td className="px-4 py-3">{new Date(o.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3">{o.name || "-"}<div className="text-zinc-500">{o.email}</div></td>
                  <td className="px-4 py-3">{o.photo_count}</td>
                  <td className="px-4 py-3">{brl(o.amount)}</td>
                  <td className="px-4 py-3">{o.coupon_code || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-600"}`}>
                      {{ paid: "Pago", expired: "Expirado", refunded: "Reembolsado", failed: "Falhou" }[o.payment_status] || "Pendente"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {o.payment_status === "paid" && (
                      <button onClick={() => refund(o)} data-testid={`refund-btn-${o.session_id}`} className="h-8 px-3 rounded-md border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50">
                        Reembolsar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && <EventForm open={form} onClose={() => setForm(false)} onSaved={load} />}
    </AdminShell>
  );
}
